import React from "react";
import Avatar from "@mui/joy/Avatar";

/**
 * The initials of a person's name: the first letter of the first and of the last word, upper case
 * ("Anna Maria Svensson" is AS). "Last, First" is read as First Last. A single word gives one letter;
 * a blank name gives an empty string.
 */
export function initialsFor(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "";
  const [before, after] = trimmed.split(",", 2);
  const ordered = after !== undefined ? `${after.trim()} ${before}` : trimmed;
  const words = ordered.split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const first = Array.from(words[0])[0] ?? "";
  const last =
    words.length > 1 ? (Array.from(words[words.length - 1])[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/** A person's name with a small initials avatar in front of it. Renders nothing for a blank name. */
export default function InitialsAvatar({ name }: { name: string }) {
  const initials = initialsFor(name);
  if (!initials) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        minWidth: 0,
      }}
    >
      <Avatar
        data-testid="initials-avatar"
        size="sm"
        variant="soft"
        color="primary"
        aria-hidden
      >
        {initials}
      </Avatar>
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </span>
    </span>
  );
}
